/**
 * Domain taxonomy for capability grouping and color-coding.
 */
export type CapabilityDomain =
  | 'cognitive'
  | 'communicative'
  | 'technical'
  | 'creative'
  | 'interpersonal'
  | 'domain_specific'
  | 'ethical'
  | 'strategic'
  | 'analytical'
  | 'meta_cognitive';

export const CAPABILITY_DOMAINS: CapabilityDomain[] = [
  'cognitive', 'communicative', 'technical', 'creative', 'interpersonal',
  'domain_specific', 'ethical', 'strategic', 'analytical', 'meta_cognitive',
];

/**
 * Domain → hex color map. Used consistently across all views.
 */
export const DOMAIN_COLORS: Record<CapabilityDomain, string> = {
  cognitive:       '#7c3aed',
  communicative:   '#2563eb',
  technical:       '#0891b2',
  creative:        '#db2777',
  interpersonal:   '#16a34a',
  domain_specific: '#d97706',
  ethical:         '#dc2626',
  strategic:       '#4338ca',
  analytical:      '#9333ea',
  meta_cognitive:  '#0d9488',
};

/**
 * Domain → Tailwind color name (for badge classes).
 */
export const DOMAIN_TAILWIND: Record<CapabilityDomain, string> = {
  cognitive:       'violet',
  communicative:   'blue',
  technical:       'cyan',
  creative:        'pink',
  interpersonal:   'green',
  domain_specific: 'amber',
  ethical:         'red',
  strategic:       'indigo',
  analytical:      'purple',
  meta_cognitive:  'teal',
};

/**
 * Rich capability report card entry.
 */
export interface CapabilityEntry {
  key: string;        // snake_case stable identifier
  label: string;      // Human-readable title-case
  score: number;      // 0-10 integer
  report: string;     // 2-3 dense paragraphs of auditor prose
  evidence: string[]; // 3-5 direct signals/quotes from the system prompt
  ceiling: string;    // One sentence: what would push score higher
  floor: string;      // One sentence: what limits it / known gaps
  domain: CapabilityDomain;
}

/**
 * Enumeration of fabrication stages in the persona creation process.
 * Represented as integers for simplicity and efficient storage.
 */
export enum FabricationStage {
  GENESIS      = 0,  // Initial creation of identity core
  TUNING       = 1,  // Adjustment of parameters and controls
  VERIFICATION = 2,  // Kept for backward compat — semantically = ARTIFACT
  PROBES       = 3,  // Diagnostic probe suite
}

/**
 * Types of UI controls available for persona parameter adjustment.
 * String enum for better serialization and readability.
 */
export enum ControlType {
  SLIDER = 'slider',    // Continuous numeric value (0.0-1.0)
  TOGGLE = 'toggle',    // Boolean on/off switch
  SELECT = 'select',    // Selection from predefined options
  TEXT = 'text'         // Free text input field
}

/**
 * Represents a single message in a conversation history.
 * Used for tracking interactions in source, cockpit, and verification panels.
 */
export interface ChatMessage {
  id: string;            // Unique identifier for the message
  role: 'user' | 'assistant' | 'system';  // Who sent the message
  content: string;       // The actual message text
  timestamp: string;     // ISO timestamp when the message was created
}

/**
 * Defines a single control in the persona's parameter adjustment interface.
 * These controls allow fine-tuning of the persona's behavior.
 */
export interface UIControl {
  id: string;            // Semantic snake_case identifier (e.g., "verbosity_level")
  label: string;         // Human-readable label (e.g., "Verbosity")
  type: ControlType;     // The type of control (slider, toggle, etc.)
  description: string;   // Tooltip/help text explaining the control
  
  // Constraints for the control
  min?: number;          // Minimum value for sliders
  max?: number;          // Maximum value for sliders
  step?: number;         // Step size for sliders
  options?: string[];    // Options for select controls
  
  // Values
  default_value: any;    // Initial value when control is created
  current_value: any;    // Current value set by the user
  
  // Metadata
  affects?: string[];    // What aspects of the persona this control affects
  rationale: string;     // Explanation of why this control exists and what it does
}

export interface ProbeRunRecord {
  id: string;
  run_at: string;                          // ISO timestamp
  input_prompt: string;
  response: string;
  passed: boolean | null;
  judged_by: 'user' | 'auto' | null;
  system_prompt_hash?: string;
  controls_snapshot?: Record<string, any>;
  notes?: string;
}

/**
 * A test case for verifying specific persona behaviors.
 * Used to validate that the persona behaves as expected.
 */
export interface DiagnosticProbe {
  id: string;
  label: string;
  input_prompt: string;
  expected_criteria: string;
  what_why: string;

  // Results from the last test run
  last_response?: string;
  last_passed?: boolean;

  // New fields
  focus_area?: string;
  generated_from_hash?: string;
  created_at?: string;
  is_custom?: boolean;
  run_history: ProbeRunRecord[];
  last_run_at?: string;
}

/**
 * The comprehensive state of a persona being engineered.
 * Contains all data needed for the persona creation process.
 */
export interface WorkingState {
  // Metadata
  id: string;
  version: number;
  last_updated: string;
  last_compiled_source_hash?: string; // Hash at last compile — used for stale detection
  last_saved?: string | null;         // ISO timestamp of last DB save

  // Stage tracking
  stage: FabricationStage;
  stage_locked: boolean;

  // Identity core (Source panel)
  character_bible: string;
  tethics: string[];
  patterns: string[];
  antipatterns: string[];

  // Conversation histories
  conversation_source: ChatMessage[];
  conversation_cockpit: ChatMessage[];
  conversation_verification: ChatMessage[];

  // Prompts and controls (Cockpit panel)
  controller_prompt: string;
  controller_prompt_hash?: string;
  ui_schema: UIControl[];
  ui_schema_hash?: string;

  // Compiled output (Artifact panel)
  system_prompt: string;
  system_prompt_hash?: string;

  // Verification / Probes
  probes: DiagnosticProbe[];
  probes_generated_from_hash?: string;
  probes_last_generated_at?: string;
  probe_suite_version?: number;

  // UI state
  active_panel_index: number;

  // Identity metadata
  display_name?: string;           // Human-readable persona name
  description?: string;            // User-facing summary
  suggested_names?: string[];      // AI-suggested name options
  tags?: string[];
  capabilities?: Record<string, CapabilityEntry>;

  // Lineage / remix chain
  origin_id?: string | null;       // ID of the persona this was remixed from
  remix_point?: string | null;     // ISO timestamp of remix
  remix_chain?: string[];          // Ordered list of ancestor persona IDs
  revision_at_fork?: number | null; // Parent's revision when this remix was created
  revision?: number;               // Incremented on each save
  created_by?: string | null;      // User ID of creator
  contributors?: string[];         // User IDs of all contributors
}

/**
 * The persistent wrapper for persona engineering data.
 * Contains the history stack and current working state.
 */
export interface EngineeringProfile {
  version: number;                // Schema version for future compatibility
  
  // We store the stack here. Frontend handles the logic of "Current Stage".
  bake_stack: WorkingState[];     // History of previous states
  
  // Current working state is always the top of the stack
  current_state: WorkingState;    // The active working state
}
